import tongxinApi from '@/apis/web/tongxin'
import store from '@/store'
import moment from 'moment'

// this file demosntrates how step machine is set up to guide page navigation
// computed property, step nodes, function calls can be extracted into individual service functions
// this file currently stores user's selection in localstorage

export default {
  inject: ['clinicIdData', 'clinicMetaData'],

  data() {
    return {
      // Step configuration
      nodes: {},
      context: {},
      localValue: null,
      isLoading: false,
      // API 資料
      apiCategories: null,
      apiSubcategories: null,
      apiSubcategoryDetail: null,
      apiAvailableDates: null,
      // 轉換後的資料
      examOptionsFromApi: [],
      outOfPocketOptionsFromApi: [],
      infoDataFromApi: {
        outer_title: '',
        subsequent_texts: []
      },
      addOnOptionsFromApi: [],
      customerServiceDataFromApi: {
        title: '',
        content: ''
      },
      noticeDataFromApi: {
        title: '',
        blocks: []
      },
      availableDatesFromApi: {},
      publicNotesFromApi: [],
      // Cache 完整的 category 樹
      cachedCategoryTree: null,
      // ExtraPage 模式標記
      isExtraInfoMode: false,
      extraInfoDataFromApi: null,
      // 唯讀模式（is_booking_available = false 時使用）
      isReadOnlyInfo: false,
      // 流程2 標記：API 2.1 就知道 isExtraInfo，直接跳過確認頁
      isDirectExtraInfo: false,
      // 緩存登入狀態
      isLoggedIn: null,
      // 掛號對象（由 family 頁回填）
      selectedRegistrant: null,
    }
  },

  computed: {
    clinicId() {
      return this.clinicIdData.value
    },

    clinicName() {
      return this.clinicMetaData?.clinicName || ''
    },

    sourceType() {
      return this.$store.getters['web/tongxin/sourceType'] || 'health'
    },

    isVaccineSource() {
      return this.sourceType === 'vaccine'
    },

    isOutpatientSource() {
      return this.sourceType === 'outpatient'
    },

    defaultExamSelectionTitle() {
      if (this.isVaccineSource) return '請選擇疫苗種類'
      if (this.isOutpatientSource) return '請選擇看診科別'
      return '請選擇體檢 / 健檢種類'
    },

    defaultNoticeTitle() {
      if (this.isVaccineSource) return '疫苗掛號須知'
      if (this.isOutpatientSource) return '門診掛號須知'
      return '體檢／健檢掛號須知'
    },

    defaultExceptionTitle() {
      if (this.isVaccineSource) return '疫苗掛號例外說明'
      if (this.isOutpatientSource) return '門診掛號例外說明'
      return '體檢 / 健檢掛號例外說明'
    },

    visitPurposeText() {
      if (this.isVaccineSource) return '注射疫苗'
      if (this.isOutpatientSource) return ''
      return '體檢 / 健檢'
    },

    visitTypeLabel() {
      if (this.isVaccineSource) return '疫苗種類'
      if (this.isOutpatientSource) return '就診科別'
      return '體檢種類'
    },

    showVisitPurpose() {
      return !this.isOutpatientSource
    },

    showDoctorField() {
      return this.isOutpatientSource
    },

    selectedDoctorName() {
      if (!this.showDoctorField) return ''

      const reservationData = this.context.reservationResult?.data || null
      const doctorNameFromReservation = this.extractDoctorNameFromReservation(reservationData)
      if (doctorNameFromReservation) return doctorNameFromReservation

      if (this.context.selectedDoctorName) return this.context.selectedDoctorName

      return '由診所安排'
    },

    currentNode() {
      return this.nodes[this.stepId] || null
    },

    resolvedProps() {
      if (!this.currentNode) return {}
      const p = this.currentNode.props
      return typeof p === 'function' ? p(this.context) : (p || {})
    },

    componentProps() {
      if (!this.currentNode) return {}
      return this.currentNode.usesValue
        ? Object.assign({}, this.resolvedProps, { value: this.localValue })
        : this.resolvedProps
    },

    canProceed() {
      const node = this.currentNode
      if (!node) return false

      const result = typeof node.validate === 'function'
        ? node.validate(this.context, this.localValue)
        : true

      return result
    },

    textForNextStep() {
      const node = this.currentNode
      if (!node || !node.textForNextStep) return '下一步'
      return typeof node.textForNextStep === 'function'
        ? node.textForNextStep.call(this)
        : node.textForNextStep
    },

    backButtonIsVisible() {
      const node = this.currentNode
      // 如果 node 有明確設定 backButtonIsVisible，使用該值
      if (node && node.backButtonIsVisible !== undefined) {
        return typeof node.backButtonIsVisible === 'function'
          ? node.backButtonIsVisible.call(this)
          : node.backButtonIsVisible
      }
      // exam-selection 時，如果是 root level（沒有 categoryId）則不顯示返回按鈕
      if (this.stepId === 'exam-selection') {
        const { categoryId } = this.$route.params
        return !!categoryId
      }
      // 其他 step 預設顯示返回按鈕
      return true
    },

    nextButtonVisible() {
      const node = this.currentNode

      // 如果 node 有明確設定 nextButtonVisible，使用該值
      if (node && node.nextButtonVisible !== undefined) {
        // 如果是 function，調用它；否則直接返回
        return typeof node.nextButtonVisible === 'function'
          ? node.nextButtonVisible.call(this)
          : node.nextButtonVisible
      }

      // 預設顯示下一步按鈕
      return true
    },

    selectedExamTypeText() {
      const { categoryId } = this.$route.params
      if (!categoryId || !this.cachedCategoryTree) return '未知體檢類型'

      const pathObjects = this.findCategoryPath(this.cachedCategoryTree.categories, categoryId)
      let breadcrumbs = []
      if (pathObjects) {
        breadcrumbs = pathObjects.map(p => p.categoryName)
      }

      if (this.isOutpatientSource) {
        if (this.context.selectedDepartmentName) {
          return this.context.selectedDepartmentName
        }

        if (breadcrumbs.length > 0) {
          const departmentIndex = Math.max(0, breadcrumbs.length - 2)
          return breadcrumbs[departmentIndex]
        }

        return '未知就診科別'
      }

      // health / vaccine：優先使用 API 2.2 的 packageName
      // health：預設只顯示末層；僅在末層為男性/女性時顯示「上一層(末層)」
      if (this.infoDataFromApi && this.infoDataFromApi.outer_title) {
        const packageName = String(this.infoDataFromApi.outer_title).trim()
        if (this.sourceType === 'health' && breadcrumbs.length >= 2) {
          const leafName = breadcrumbs[breadcrumbs.length - 1]
          const parentName = breadcrumbs[breadcrumbs.length - 2]
          const shouldComposeWithParent = ['男性', '女性'].includes(leafName)
          if (packageName === leafName && parentName && shouldComposeWithParent) {
            return `${parentName}(${leafName})`
          }
        }
        return packageName
      }

      if (breadcrumbs.length >= 4) {
        return `${breadcrumbs[2]} (${breadcrumbs[3]})`
      } else if (breadcrumbs.length >= 3) {
        return breadcrumbs[2]
      } else if (breadcrumbs.length >= 2) {
        return breadcrumbs[1]
      }

      const category = this.findCategoryById(this.cachedCategoryTree.categories, categoryId)
      return category ? category.categoryName : '未知體檢類型'
    },

    selectedAddOnNames() {
      if (!Array.isArray(this.context.addOns)) return []

      return this.context.addOns
        .map(id => {
          const opt = this.addOnOptionsFromApi.find(o => o.value === id)
          return opt ? opt.label : null
        })
        .filter(Boolean)
    },

    selectedPlanBaseFee() {
      // 從 API 2.2 (apiSubcategoryDetail) 的 packageBlocks 中找出 title === '費用' 的 block
      // 并將 notes 轉換成數字
      if (this.apiSubcategoryDetail && this.apiSubcategoryDetail.data) {
        const blocks = this.apiSubcategoryDetail.data.packageBlocks
        if (Array.isArray(blocks)) {
          const feeBlock = blocks.find(b => b.title === '費用')
          if (feeBlock && feeBlock.notes !== null && feeBlock.notes !== undefined) {
            const parsed = parseInt(String(feeBlock.notes).replace(/[^0-9]/g, ''), 10)
            if (!isNaN(parsed)) return parsed
          }
        }
      }
      return 0
    },

    selectedCarryItemsText() {
      if (!this.apiSubcategoryDetail || !this.apiSubcategoryDetail.data) return ''

      const blocks = this.apiSubcategoryDetail.data.packageBlocks
      if (!Array.isArray(blocks)) return ''

      const carryBlock = blocks.find(b => (b?.title || '').replace(/<[^>]*>/g, '').trim() === '應攜帶')
      if (!carryBlock) return ''

      const rawText = carryBlock.textContent || carryBlock.notes || ''
      return String(rawText).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
    },
  },

  methods: {
    getDefaultExceptionContent() {
      if (this.isVaccineSource) {
        return '因您的疫苗預約需由診所進一步確認，所以目前無法直接讓您選擇時段。\n\n請先於下一頁選擇需要掛號的人員，診所人員將在後續協助您確認時段。'
      }
      if (this.isOutpatientSource) {
        return '因您的門診預約需由診所進一步確認，所以目前無法直接讓您選擇時段。\n\n請先於下一頁選擇需要掛號的人員，診所人員將在後續協助您確認時段。'
      }
      return '因您的健檢含特殊項目，檢查需要排時間（例：大腸鏡）且可能有檢查前注意事項，所以無法直接讓您選擇時段。\n\n請先於下一頁選擇需要掛號的人員，診所人員將在後續協助您確認時段。'
    },

    extractDoctorNameFromReservation(reservationData) {
      if (!reservationData) return null

      if (typeof reservationData.doctorName === 'string' && reservationData.doctorName.trim()) {
        return reservationData.doctorName.trim()
      }

      const infoBlock = Array.isArray(reservationData.infoBlock) ? reservationData.infoBlock : []
      const doctorItem = infoBlock.find(item => {
        const title = String(item?.title || '')
        return title.includes('醫師')
      })

      if (doctorItem && doctorItem.text) {
        return String(doctorItem.text).trim() || null
      }

      return null
    },

    goToLineOfficialAccount() {
      const orgCode = this.$route.params.orgCode || this.$route.params.org_code

      if (orgCode) {
        window.location.href = `${process.env.VUE_APP_API_BASE_URL}/${orgCode}/liff/general/redirect-to-oa`
        return
      }

      if (window.liff && typeof window.liff.closeWindow === 'function') {
        window.liff.closeWindow()
      }
    },

    getNextStepAfterInfo() {
      if (this.addOnOptionsFromApi && this.addOnOptionsFromApi.length > 0) {
        return 'add-ons'
      }

      if (this.isExtraInfoMode) {
        return 'extra-info'
      }

      return 'notice'
    },

    shouldSkipInfoPage(categoryId) {
      if (!categoryId || !this.cachedCategoryTree) return false

      const category = this.findCategoryById(this.cachedCategoryTree.categories, categoryId)
      return !!(category && category.skipInfoPage === true)
    },

    backToExamSelectionParent(categoryId) {
      const parent = this.findParentCategory(
        this.cachedCategoryTree.categories,
        categoryId
      )

      if (parent) {
        this.$router.push({
          name: 'TongxinStep',
          params: {
            org_code: this.$route.params.org_code,
            stepId: 'exam-selection',
            categoryId: parent.categoryId
          }
        })
        return
      }

      this.$router.push({
        name: 'TongxinStep',
        params: {
          org_code: this.$route.params.org_code,
          stepId: 'exam-selection'
        }
      })
    },

    // Initialize step nodes from mock data (will be replaced with API call)
    initializeMockSteps() {
      this.nodes = {
        'exam-selection': {
          component: 'SelectionStep',
          usesValue: true,
          props: () => {
            let pageTitle = this.defaultExamSelectionTitle
            let categoryImages = []
            const { categoryId } = this.$route.params

            if (!categoryId) {
              categoryImages = this.extractScheduleImages(this.cachedCategoryTree?.showImgInfo || [])
            }

            if (categoryId && this.cachedCategoryTree) {
              const category = this.findCategoryById(this.cachedCategoryTree.categories, categoryId)
              if (category && category.categoryName) {
                pageTitle = category.categoryName
              }

              const isLastCategoryWithChildren = (category) => (category?.children || []).length > 0
                && category.children.every(child => (child?.children || []).length === 0)

              // If outpatient and leaf node (no children), show doctor selection title
              if (this.isOutpatientSource && isLastCategoryWithChildren(category)) {
                pageTitle = this.getDoctorListTitle(category)
              }

              if (this.isOutpatientSource && (category?.children || []).every(isLastCategoryWithChildren)) {
                pageTitle = '請選擇看診科別'
              }
            }
            return {
              title: pageTitle,
              items: this.examOptionsFromApi,
              images: categoryImages,
            }
          },
          initValue: () => null,
          validate: (ctx, val) => !!val,
          onBack: () => {
            const { categoryId } = this.$route.params

            if (!categoryId) {
              // Root level，返回上一頁（離開 tongxin flow）
              this.$router.back()
            } else {
              // 尋找 parent category
              const parent = this.findParentCategory(this.cachedCategoryTree.categories, categoryId)

              if (!parent) {
                // 沒有 parent，返回 root level
                this.$router.push({
                  name: 'TongxinStep',
                  params: {
                    org_code: this.$route.params.org_code,
                    stepId: 'exam-selection'
                  }
                })
              } else {
                // 有 parent，navigate 去 parent
                this.$router.push({
                  name: 'TongxinStep',
                  params: {
                    org_code: this.$route.params.org_code,
                    stepId: 'exam-selection',
                    categoryId: parent.categoryId
                  }
                })
              }
            }
          },
          onNext: async (ctx, selected) => {
            const selectedCategory = this.examOptionsFromApi.find(item => item.value === selected)
            const fullCategory = this.findCategoryById(this.cachedCategoryTree.categories, selected)
            const { categoryId: currentCategoryId } = this.$route.params
            const currentCategory = currentCategoryId
              ? this.findCategoryById(this.cachedCategoryTree.categories, currentCategoryId)
              : null

            if (this.isOutpatientSource) {
              // 當前層級為「選醫師」時，leaf 即醫師；parent 即科別
              if (this.isDoctorSelectionLevel(currentCategory) && fullCategory && !fullCategory.hasChildren) {
                this.invalidateAvailableDates()
                Object.assign(ctx, {
                  selectedDepartmentName: currentCategory?.categoryName || '',
                  selectedDoctorName: fullCategory.categoryName || ''
                })
              } else if (fullCategory && fullCategory.hasChildren) {
                // 選到下一層時先清空醫師，避免舊值殘留
                Object.assign(ctx, { selectedDoctorName: '' })

                // 若此節點的 children 全為 leaf，表示目前選中的是「科別」
                if (this.isDoctorSelectionLevel(fullCategory)) {
                  Object.assign(ctx, { selectedDepartmentName: fullCategory.categoryName || '' })
                }
              }
            }

            // 1. 有 children → navigate 到該子分類
            if (selectedCategory && selectedCategory.hasChildren) {
              this.$router.push({
                name: 'TongxinStep',
                params: { ...this.$route.params, categoryId: selected }
              })
              return
            }

            // 流程3：is_booking_available = false → 導向 info 唯讀模式（無下一步按鈕）
            if (fullCategory && fullCategory.is_booking_available === false) {
              this.isExtraInfoMode = false
              this.isDirectExtraInfo = false
              this.isReadOnlyInfo = true
              this.$router.push({
                name: 'TongxinStep',
                params: {
                  org_code: this.$route.params.org_code,
                  stepId: 'info',
                  categoryId: selected
                },
                query: {
                  ...this.$route.query,
                  readonlyInfo: '1'
                },
              })
              return
            }

            // 流程1A / 1B / 2：is_booking_available = true
            // 先打 API 2.2 取得實際內容，再決定路由
            try {
              this.isLoading = true
              const detail = await this.loadSubcategoryDetail(selected)

              // 取得 category path breadcrumbs
              const pathObjects = this.findCategoryPath(this.cachedCategoryTree.categories, selected)
              let breadcrumbs = []
              if (pathObjects) {
                breadcrumbs = pathObjects.map(p => p.categoryName)
              }

              this.isExtraInfoMode = !!detail.data.isExtraInfo
              this.infoDataFromApi = {
                ...this.transformSubcategoryToInfo(detail.data),
                breadcrumbs
              }
              this.addOnOptionsFromApi = this.transformOptionsToAddOns(detail.data)
              this.publicNotesFromApi = this.transformPublicNotes(detail.data.publicNotesPage)
              const extraPageData = this.transformExtraPageBinding(detail.data.extraPage)
              const extraPageBindingData = this.transformExtraPageBinding(detail.data.extraPageBinding)
              const isLoggedIn = await this.checkLoginStatus();

              if (isLoggedIn && extraPageBindingData) {
                this.extraInfoDataFromApi = extraPageBindingData
              } else if (extraPageData) {
                this.extraInfoDataFromApi = extraPageData
              } else {
                this.extraInfoDataFromApi = null
              }
              this.isReadOnlyInfo = false
            } catch (err) {
              console.error('Failed to pre-fetch API 2.2:', err)
              return
            } finally {
              this.isLoading = false
            }

            this.syncDirectExtraInfoMode()

            // 流程2：isExtraInfo = true 且 無圖文、無加購 → 直接跳 extra-info
            if (this.isDirectExtraInfo) {
              this.$router.push({
                name: 'TongxinStep',
                params: {
                  org_code: this.$route.params.org_code,
                  stepId: 'extra-info',
                  categoryId: selected
                }
              })
              return
            }

            if (this.shouldSkipInfoPage(selected)) {
              this.$router.push({
                name: 'TongxinStep',
                params: {
                  org_code: this.$route.params.org_code,
                  stepId: this.getNextStepAfterInfo(),
                  categoryId: selected
                }
              })
              return
            }

            // 流程1A / 1B：有圖文或加購 → 進 info（API 2.2 已預載完畢）
            this.$router.push({
              name: 'TongxinStep',
              params: {
                org_code: this.$route.params.org_code,
                stepId: 'info',
                categoryId: selected
              }
            })
          },
        },
        'oop-selection': {
          component: 'SelectionStep',
          usesValue: true,
          props: () => ({
            title: '請選擇加值自費方案',
            items: this.outOfPocketOptionsFromApi,
          }),
          initValue: () => null,
          validate: (ctx, val) => !!val,
          onBack: () => {
            // 返回 exam-selection，保留 route params
            return 'exam-selection'
          },
          onNext: (ctx, selected) => {
            Object.assign(ctx, { outOfPocketPlan: selected })
            // 儲存選中的方案資訊到 context
            const selectedPlan = this.outOfPocketOptionsFromApi.find(item => item.value === selected)
            if (selectedPlan) {
              Object.assign(ctx, { outOfPocketPlanData: selectedPlan })
            }
            return 'info-teen'
          },
        },
        'info': {
          component: 'Info',
          usesValue: false,
          props: () => ({ data: this.infoDataFromApi }),
          onBack: () => {
            // 返回時清除唯讀標記
            this.isReadOnlyInfo = false

            // 從 route params 讀取當前的 categoryId (subcategoryId)
            const { categoryId } = this.$route.params

            // 返回 exam-selection，並帶上 parent categoryId（如果有的話）
            const parent = this.findParentCategory(
              this.cachedCategoryTree.categories,
              categoryId
            )

            if (parent) {
              this.$router.push({
                name: 'TongxinStep',
                params: {
                  org_code: this.$route.params.org_code,
                  stepId: 'exam-selection',
                  categoryId: parent.categoryId
                }
              })
            } else {
              this.$router.push({
                name: 'TongxinStep',
                params: {
                  org_code: this.$route.params.org_code,
                  stepId: 'exam-selection'
                }
              })
            }
          },
          onNext: () => {
            // 如果有 options，去 add-ons；否則若是 isExtraInfoMode 跳去 extra-info；否則跳去 notice
            return this.getNextStepAfterInfo()
          },
          // 流程3：唯讀模式時隱藏下一步按鈕
          nextButtonVisible: function() {
            return !this.isReadOnlyInfo
          },
          textForNextStep: '下一步',
        },
        'add-ons': {
          component: 'AddOn',
          usesValue: true,
          initValue: () => [],
          props: () => ({
            baseFee: this.selectedPlanBaseFee,
            options: this.addOnOptionsFromApi,
          }),
          validate: () => true,
          onBack: () => {
            const { categoryId } = this.$route.params

            if (this.shouldSkipInfoPage(categoryId)) {
              this.backToExamSelectionParent(categoryId)
              return null
            }

            return 'info'
          },
          onNext: (ctx, selected) => {
            Object.assign(ctx, { addOns: Array.isArray(selected) ? selected.slice() : [] })
            if (this.isExtraInfoMode) {
              return 'extra-info'
            }
            return 'notice'
          },
        },
        'extra-info': {
          component: 'CustomerServiceConfirmation',
          usesValue: false,
          props: () => ({
            data: this.extraInfoDataFromApi || {
              title: this.defaultExceptionTitle,
              content: this.getDefaultExceptionContent()
            }
          }),
          onBack: () => {
            if (this.addOnOptionsFromApi && this.addOnOptionsFromApi.length > 0) {
              return 'add-ons'
            }

            const { categoryId } = this.$route.params

            if (this.shouldSkipInfoPage(categoryId)) {
              this.backToExamSelectionParent(categoryId)
              return null
            }

            if (this.hasInfoData()) {
              return 'info'
            }

            const category = this.findCategoryById(this.cachedCategoryTree.categories, this.$route.params.categoryId)

            if (category.children?.length > 0) {
              return 'exam-selection'
            }

            // 此頁面無 children，嘗試回到再上一層的選單
            const parent = this.findParentCategory(this.cachedCategoryTree.categories, this.$route.params.categoryId)

            if (parent) {
              // 有 parent，navigate 去 parent
              this.$router.push({
                name: 'TongxinStep',
                params: {
                  org_code: this.$route.params.org_code,
                  stepId: 'exam-selection',
                  categoryId: parent.categoryId
                }
              })
            } else {
              // 沒有 parent，返回 root level
              this.$router.push({
                name: 'TongxinStep',
                params: {
                  org_code: this.$route.params.org_code,
                  stepId: 'exam-selection'
                }
              })
            }
          },
          onNext: async () => {
            // 流程2：直接例外模式也需先進掛號對象頁，再回簡化版 success
            if (this.isDirectExtraInfo) {
              await this.routeToRegistrantSelection('extra-info', 'success')
              return null
            }
            // 流程1B：進入掛號對象頁，完成後再回 user-notes/confirmation
            await this.routeToRegistrantSelection('extra-info')
            return null
          },
          textForNextStep: '下一步 >',
        },
        'notice': {
          component: 'Notice',
          usesValue: false,
          props: () => ({ data: this.noticeDataFromApi }),
          onBack: () => {
            // 如果有 addOns 選項，返回 add-ons；否則返回 info 或是 extra-info
            if (this.addOnOptionsFromApi && this.addOnOptionsFromApi.length > 0) {
              return 'add-ons'
            } else if (this.isExtraInfoMode) {
              return 'extra-info'
            } else if (this.shouldSkipInfoPage(this.$route.params.categoryId)) {
              this.backToExamSelectionParent(this.$route.params.categoryId)
              return null
            } else {
              return 'info'
            }
          },
          onNext: () => 'date-selection',
          textForNextStep: '我已瞭解',
        },
        'date-selection': {
          component: 'TongxinDateSelector',
          usesValue: true,
          initValue: () => null,
          props: () => {
            // 從 availableDatesFromApi (Array) 提取所有可預約日期
            const availableDatesList = Array.isArray(this.availableDatesFromApi)
              ? this.availableDatesFromApi.map(item => item.date)
              : []

            return {
              availableDates: availableDatesList,
              offDutyDates: [],
              recurringOffDays: [],
              bookingWindow: {type: 'day', amount: 60}
            }
          },
          validate: (ctx, val) => !!val, // Must select a date to proceed
          onBack: () => 'notice',
          onNext: (ctx, selected) => {
            Object.assign(ctx, { selectedDate: selected })
            return 'time-selection'
          },
        },
        'time-selection': {
          component: 'AppointmentTimeSelector',
          usesValue: true,
          initValue: () => null,
          props: () => ({
            dates: this.availableDatesFromApi,
            loading: false,
            selectedDate: this.context.selectedDate
          }),
          validate: (ctx, val) => !!val,
          onBack: () => 'date-selection',
          onNext: async (ctx, selected) => {
            Object.assign(ctx, { selectedTimeSlot: selected })
            // 流程1A：進入掛號對象頁，完成後再回 user-notes/confirmation
            await this.routeToRegistrantSelection('time-selection')
            return null
          },
        },
        'registrant-gate': {
          component: 'CustomerServiceConfirmation',
          usesValue: false,
          props: () => ({
            data: {
              title: '掛號對象',
              content: '正在前往掛號對象頁面，請稍候。'
            }
          }),
          onBack: () => this.context.registrantBackStep || (this.isExtraInfoMode ? 'extra-info' : 'time-selection'),
          onNext: async () => {
            await this.routeToRegistrantSelection(this.context.registrantBackStep || (this.isExtraInfoMode ? 'extra-info' : 'time-selection'))
            return null
          },
          backButtonIsVisible: false,
          nextButtonVisible: false,
        },
        'user-notes': {
          component: 'UserNotes',
          usesValue: true,
          initValue: () => null,
          props: () => ({
            notesConfig: this.publicNotesFromApi
          }),
          validate: () => true,
          onBack: () => {
            return 'registrant-gate'
          },
          onNext: (ctx, selected) => {
            Object.assign(ctx, { userNotes: selected })
            return 'confirmation'
          },
        },
        'confirmation': {
          component: 'Confirmation',
          usesValue: false,
          props: () => ({
            clinicName: this.clinicName,
            visitPurpose: this.visitPurposeText,
            visitTypeLabel: this.visitTypeLabel,
            showVisitPurpose: this.showVisitPurpose,
            showDoctor: this.showDoctorField,
            doctorName: this.selectedDoctorName,
            examTypeText: this.selectedExamTypeText,
            visitTimeSlot: this.isExtraInfoMode ? '由診所人員協助確認' : this.context.selectedTimeSlot,
            patientName: this.getSelectedPatientDisplayName(),
            totalCost: this.selectedPlanBaseFee + this.calculateAddOnsCost(),
            selectedAddOns: this.selectedAddOnNames,
          }),
          onBack: () => {
            if (this.publicNotesFromApi && this.publicNotesFromApi.length > 0) {
              return 'user-notes'
            }
            return 'registrant-gate'
          },
          onNext: async (ctx) => {
            try {
              // Call API 2.4 建立預約
              this.isLoading = true
              const response = await this.createReservation()
              this.isLoading = false

              // 將預約結果儲存到 context
              Object.assign(ctx, { reservationResult: response })

              return 'success'
            } catch (error) {
              console.error('建立預約失敗:', error)
              const failResponse = error && error.response && error.response.data
              if (failResponse && String(failResponse.status || '').toLowerCase() === 'fail') {
                Object.assign(ctx, { reservationResult: failResponse })
                this.isLoading = false
                return 'success'
              }
              this.$swal({
                type: 'error',
                title: '預約失敗，請稍後再試',
                confirmButtonText: '確定'
              })
              this.isLoading = false
              return null  // 停留在當前頁面
            }
          },
          textForNextStep: '確認預約',
        },
        'success': {
          component: 'Success',
          usesValue: false,
          props: () => ({
            simpleMode: this.isDirectExtraInfo,
            clinicName: this.clinicName,
            visitPurpose: this.visitPurposeText,
            visitTypeLabel: this.visitTypeLabel,
            showVisitPurpose: this.showVisitPurpose,
            showDoctor: this.showDoctorField,
            doctorName: this.selectedDoctorName,
            examTypeText: this.selectedExamTypeText,
            visitTimeSlot: this.isExtraInfoMode ? '由診所人員協助確認' : this.context.selectedTimeSlot,
            patientName: this.getSelectedPatientDisplayName(),
            totalCost: this.selectedPlanBaseFee + this.calculateAddOnsCost(),
            carryItemsText: this.selectedCarryItemsText,
            selectedAddOns: this.selectedAddOnNames,
            noticeList: this.context.reservationResult?.data?.noticeList || [],
            reservationInfoBlock: this.context.reservationResult?.data?.infoBlock || [],
            reservationResult: this.context.reservationResult || null,
            contactPhone: this.context?.registrant?.mobile || this.context?.contactMobile || '',
          }),
          onBack: () => {
            if (this.isDirectExtraInfo) {
              return 'extra-info'
            }
            return 'confirmation'
          },
          onNext: (ctx) => {
            console.log(ctx)
            this.goToLineOfficialAccount()
            return null;
          },
          backButtonIsVisible: function() {
            return String(this.context?.reservationResult?.status || '').toLowerCase() === 'fail'
          },
          textForNextStep: function() {
            if (String(this.context?.reservationResult?.status || '').toLowerCase() === 'fail') {
              return '回 LINE 官方帳號'
            }
            return this.isDirectExtraInfo ? '關閉' : '回 LINE 官方帳號'
          },
        },
        'customer-service': {
          component: 'CustomerServiceConfirmation',
          usesValue: false,
          props: () => ({ data: this.customerServiceDataFromApi }),
          onBack: () => {
            // 返回 exam-selection，保留 categoryId（讓使用者重新選擇）
            const { categoryId } = this.$route.params
            this.$router.push({
              name: 'TongxinStep',
              params: {
                org_code: this.$route.params.org_code,
                stepId: 'exam-selection',
                categoryId: categoryId || undefined
              }
            })
          },
          onNext: () => {
            this.goToLineOfficialAccount()
            return null
          },
          textForNextStep: '下一步 >',
        },
      }
    },

    // Future method: Load steps from API
    async loadStepsFromAPI() {
      try {
        this.isLoading = true
        // TODO: Replace with actual API call
        // const response = await this.$api.tongxin.getSteps()
        // this.nodes = this.transformAPIStepsToNodes(response.data)

        // For now, fall back to mock data
        this.initializeMockSteps()
      } catch (error) {
        console.error('Failed to load steps from API:', error)
        // Fall back to mock data on error
        this.initializeMockSteps()
      } finally {
        this.isLoading = false
      }
    },


    // transformAPIStepsToNodes(apiSteps) {
      // TODO: Transform API response to match the expected node structure
      // This will depend on your API response format
    // },

    // Step navigation methods
    onValueChange(v) {
      this.localValue = v;
      // Force reactivity update
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },

    async enterNode(id) {
      // 確保 clinicId 已設定
      if (!this.clinicId) {
        console.error('enterNode called but clinicId is not set')
        return
      }

      this.isLoading = true

      try {
        // 根據不同的 step 載入對應的 API 資料
        if (id === 'exam-selection') {
          // 從 route params 讀取當前的 categoryId
          const { categoryId } = this.$route.params

          // 如果未 cache，先 call API 拎完整樹
          if (!this.cachedCategoryTree) {
            const categories = await this.loadCategories(null, null)
            this.cachedCategoryTree = categories.data
          }

          let dataToTransform

          if (!categoryId) {
            // Root level - 直接使用 cache 的 root categories
            dataToTransform = {
              ...this.cachedCategoryTree,
              categories: this.sortCategoriesBySortOrder(this.cachedCategoryTree.categories)
            }
          } else {
            // 有 categoryId - 從 cache 尋找對應的 children
            const category = this.findCategoryById(this.cachedCategoryTree.categories, categoryId)
            if (category && category.children) {
              dataToTransform = {
                categories: this.sortCategoriesBySortOrder(category.children)
              }
            } else {
              dataToTransform = { categories: [] }
            }
          }

          this.examOptionsFromApi = this.transformCategoriesToItems(dataToTransform)
        } else if (id === 'notice') {
          // categoryId 係 subcategoryId (leaf category)，用作 API 2.3 的 rowId
          const { categoryId } = this.$route.params

          if (categoryId) {
            // 只在未載入資料時 call API
            if (!this.apiAvailableDates) {
              console.log('Calling API 2.3 - getAvailableDates with rowId (subcategoryId):', categoryId)
              try {
                const dates = await this.loadAvailableDates(categoryId)
                console.log('API 2.3 response:', dates)

                // 儲存 notice 資料和可預約日期
                this.noticeDataFromApi = {
                  title: dates.data.noticeTitle || this.defaultNoticeTitle,
                  blocks: this.transformNoticeBlocks(dates.data.noticeBlocks || [])
                }
                this.availableDatesFromApi = this.transformAvailableDates(dates.data)
                console.log('noticeDataFromApi:', this.noticeDataFromApi)
                console.log('availableDatesFromApi:', this.availableDatesFromApi)
              } catch (error) {
                console.error('Failed to load available dates:', error)
              }
            } else {
              console.log('notice step - using cached API 2.3 data')
            }
          } else {
            console.error('notice enterNode - No categoryId found in route params!')
          }
        } else if (id === 'info' || id === 'add-ons' || id === 'extra-info') {
          if (id === 'info') {
            this.isReadOnlyInfo = this.$route.query.readonlyInfo === '1'
          }

          // 確保 cachedCategoryTree 為了返回功能
          if (!this.cachedCategoryTree) {
            const categories = await this.loadCategories(null, null)
            this.cachedCategoryTree = categories.data
          }

          // 從 route params 讀取 subcategoryId
          const { categoryId } = this.$route.params

          const isApi22Loaded = this.apiSubcategoryDetail && this.apiSubcategoryDetail.data && this.apiSubcategoryDetail.data.packageId === categoryId

          if (categoryId && (!isApi22Loaded)) {
            // 載入 API 2.2 取得詳細資料
            const detail = await this.loadSubcategoryDetail(categoryId)

            // 取得 category path breadcrumbs
            const pathObjects = this.findCategoryPath(this.cachedCategoryTree.categories, categoryId)
            let breadcrumbs = []
            if (pathObjects) {
              breadcrumbs = pathObjects.map(p => p.categoryName)
            }

            this.isExtraInfoMode = !!detail.data.isExtraInfo
            this.infoDataFromApi = {
              ...this.transformSubcategoryToInfo(detail.data),
              breadcrumbs
            }
            this.addOnOptionsFromApi = this.transformOptionsToAddOns(detail.data)
            this.publicNotesFromApi = this.transformPublicNotes(detail.data.publicNotesPage)
            const extraPageData = this.transformExtraPageBinding(detail.data.extraPage)
            const extraPageBindingData = this.transformExtraPageBinding(detail.data.extraPageBinding)
            const isLoggedIn = await this.checkLoginStatus();

            if (isLoggedIn && extraPageBindingData) {
              this.extraInfoDataFromApi = extraPageBindingData
            } else if (extraPageData) {
              this.extraInfoDataFromApi = extraPageData
            } else {
              this.extraInfoDataFromApi = null
            }
            this.syncDirectExtraInfoMode()


            // 若 info 頁無實際內容且不是唯讀模式，自動跳轉到下一步
            if (id === 'info' && !this.isReadOnlyInfo && !this.hasInfoData()) {
              let nextStep = 'notice'
              if (this.addOnOptionsFromApi && this.addOnOptionsFromApi.length > 0) {
                // 有 options 但沒有 packageBlocks → 不太可能，但走 add-ons 保險
                nextStep = 'add-ons'
              } else if (this.isDirectExtraInfo) {
                // 沒有 packageBlocks 也沒有 options，且是例外模式 → 流程2 直達例外頁
                nextStep = 'extra-info'
              }
              this.$nextTick(() => {
                this.$router.replace({
                  name: 'TongxinStep',
                  params: {
                    ...this.$route.params,
                    stepId: nextStep
                  }
                })
              })
              return
            }
          }
        } else if (id === 'customer-service') {
          // 流程4：is_booking_available = false → 轉客服確認頁2
          // 顯示靜態說明文字（不需 call API）
          const { categoryId } = this.$route.params
          let categoryName = ''
          if (categoryId && this.cachedCategoryTree) {
            const cat = this.findCategoryById(this.cachedCategoryTree.categories, categoryId)
            categoryName = cat ? cat.categoryName : ''
          }
          this.customerServiceDataFromApi = {
            title: this.defaultExceptionTitle,
            content: [
              categoryName ? `您選擇的「${categoryName}」目前暫不提供線上預約。` : '',
              this.isOutpatientSource
                ? '如果您對需要就診的科別不確定，可繼續點選下一步，'
                : '如果您對自己需要的健檢項目不瞭解，或上頁健檢套組並沒有符合您的需求，可繼續點選下一步，',
              '系統將會發送訊息通知客服專員，我們會於上班時間儘速與您聯絡！',
              '',
              this.isOutpatientSource ? '請確認「看診對象」正確，我們將以此對象評估。' : '請確認「健檢對象」正確，我們將以此對象評估。'
            ].filter(Boolean).join('\n')
          }
        } else if (id === 'date-selection' || id === 'time-selection') {
          // date-selection 同 time-selection 都需要 API 2.3 資料
          const { categoryId } = this.$route.params

          if (categoryId) {
            // 只在未載入資料時 call API
            if (!this.apiAvailableDates) {
              console.log(`${id} - Calling API 2.3 with categoryId:`, categoryId)
              try {
                const dates = await this.loadAvailableDates(categoryId)
                console.log('API 2.3 response:', dates)

                // 儲存 notice 資料和可預約日期
                this.noticeDataFromApi = {
                  title: dates.data.noticeTitle || this.defaultNoticeTitle,
                  blocks: this.transformNoticeBlocks(dates.data.noticeBlocks || [])
                }
                this.availableDatesFromApi = this.transformAvailableDates(dates.data)
                console.log('availableDatesFromApi:', this.availableDatesFromApi)
              } catch (error) {
                console.error('Failed to load available dates:', error)
              }
            } else {
              console.log(`${id} - using cached API 2.3 data`)
            }
          } else {
            console.error(`${id} - No categoryId found in route params!`)
          }
        } else if (id === 'registrant-gate') {
          await this.routeToRegistrantSelection(this.context.registrantBackStep || (this.isExtraInfoMode ? 'extra-info' : 'time-selection'))
          return
        } else if (id === 'user-notes' || id === 'confirmation' || id === 'success') {
          const { categoryId } = this.$route.params
          if (categoryId) {
            try {
              // 從 family 頁回來時，mixin 會重建，這裡補齊 confirmation/success 需要的資料
              if (!this.cachedCategoryTree) {
                const categories = await this.loadCategories(null, null)
                this.cachedCategoryTree = categories.data
              }

              const isApi22Loaded = this.apiSubcategoryDetail &&
                this.apiSubcategoryDetail.data &&
                this.apiSubcategoryDetail.data.packageId === categoryId

              const needReloadApi22 =
                !isApi22Loaded ||
                !Array.isArray(this.addOnOptionsFromApi) ||
                this.addOnOptionsFromApi.length === 0 ||
                !Array.isArray(this.publicNotesFromApi)

              if (needReloadApi22) {
                const detail = await this.loadSubcategoryDetail(categoryId)
                this.isExtraInfoMode = !!detail.data.isExtraInfo
                this.infoDataFromApi = this.transformSubcategoryToInfo(detail.data)
                this.addOnOptionsFromApi = this.transformOptionsToAddOns(detail.data)
                this.publicNotesFromApi = this.transformPublicNotes(detail.data.publicNotesPage)
              } else if (!this.publicNotesFromApi || this.publicNotesFromApi.length === 0) {
                const detail = await this.loadSubcategoryDetail(categoryId)
                this.isExtraInfoMode = !!detail.data.isExtraInfo
                this.infoDataFromApi = this.transformSubcategoryToInfo(detail.data)
                this.addOnOptionsFromApi = this.transformOptionsToAddOns(detail.data)
                this.publicNotesFromApi = this.transformPublicNotes(detail.data.publicNotesPage)
              }
              this.syncDirectExtraInfoMode()

            } catch (error) {
              console.error(`Failed to load data for ${id}:`, error)
            }
          }
        }

        // 設定 localValue
        const node = this.nodes[id]
        if (node && typeof node.initValue === 'function') {
          this.localValue = node.initValue(this.context)
        } else {
          this.localValue = node && node.usesValue ? null : null
        }

        // Force reactivity update after setting localValue
        this.$nextTick(() => {
          if (this.localValue !== undefined) {
            this.$forceUpdate()
          }
        })
      } catch (error) {
        console.error('Failed to load data for step:', id, error)
        throw error
      } finally {
        this.isLoading = false
      }
    },

    async handleNext() {
      const node = this.currentNode
      if (!node || !node.onNext || !this.canProceed) return

      const next = await node.onNext(this.context, this.localValue)

      // Force reactivity update and save
      this.$set(this, 'context', { ...this.context })
      this.saveContext()

      if (typeof next === 'string') {
        // 返回 step ID，navigation 到該 step
        this.$router.push({
          name: 'TongxinStep',
          params: {
            ...this.$route.params,
            stepId: next
          }
        })
        this.localValue = null
      }
      // 如果返回 undefined/null，表示 onNext 已經用 router.push 處理 navigation
    },

    async handleBack() {
      const node = this.currentNode
      if (node && typeof node.onBack === 'function') {
        const prev = await node.onBack(this.context)

        // Force reactivity update and save
        this.$set(this, 'context', { ...this.context })
        this.saveContext()

        if (typeof prev === 'string') {
          // 返回 step ID，navigation 到該 step
          this.$router.push({
            name: 'TongxinStep',
            params: {
              ...this.$route.params,
              stepId: prev
            }
          })
        }
        // 如果返回 undefined/null，表示 onBack 已經用 router.push 處理 navigation
      }
    },

    // Context persistence
    saveContext() {
      try {
        // Save both context and current localValue
        const dataToSave = {
          context: this.context,
          currentStepId: this.stepId,
          localValue: this.localValue
        };
        localStorage.setItem('tongxin-context', JSON.stringify(dataToSave))
      } catch (e) {
        console.warn('Failed to save context to localStorage:', e)
      }
    },

    loadContext() {
      try {
        const saved = localStorage.getItem('tongxin-context')
        if (saved) {
          const parsed = JSON.parse(saved)
          // Load context
          if (parsed.context) {
            this.context = parsed.context
          }
          // restore localValue if we're on the same step
          if (parsed.currentStepId === this.stepId && parsed.localValue !== undefined) {
            this.localValue = parsed.localValue
          }
        }
      } catch (e) {
        console.warn('Failed to load context from localStorage:', e)
      }
    },

    // getAvailableDates() {
    //   // TODO: This could come from an API call based on the selected package
    // },


    // getConfirmationData() {
    //   // TODO: Implement confirmation data logic
    // },


    // async submitAppointment() {
    //   // TODO: Replace with actual API call
    // },

    // Simplify confirmation props calculation
    calculateConfirmationProps(ctx) {
      return {
        examTypeText: this.selectedExamTypeText,
        visitTimeSlot: ctx.selectedTimeSlot,
        totalCost: this.selectedPlanBaseFee + this.calculateAddOnsCost(),
      };
    },

    // ===== 輔助判斷方法 =====

    /**
     * 檢查當前使用者的 LINE 登入狀態
     */
    async checkLoginStatus() {
      if (this.isLoggedIn !== null) return this.isLoggedIn;

      try {
        // 直接讀取 Vuex 裡面的 isLogin 狀態
        this.isLoggedIn = store.state.liffAuth.isLogin || false;

        if (this.isLoggedIn) {
          console.log('使用者已登入 LINE (Token 存在)');
        } else {
          console.log('使用者尚未登入 LINE');
        }
      } catch (e) {
        console.warn('登入狀態檢查失敗:', e);
        this.isLoggedIn = false;
      }
      return this.isLoggedIn;
    },

    /**
     * 判斷 info 頁是否有實際可顯示的內容
     * infoDataFromApi 是物件 { outer_title, subsequent_texts }，
     * 需要檢查 subsequent_texts 陣列是否有內容
     */
    hasInfoData() {
      if (!this.infoDataFromApi) return false
      const texts = this.infoDataFromApi.subsequent_texts
      return Array.isArray(texts) && texts.length > 0
    },

    syncDirectExtraInfoMode() {
      const hasContent = this.hasInfoData()
      const hasOptions = Array.isArray(this.addOnOptionsFromApi) && this.addOnOptionsFromApi.length > 0
      this.isDirectExtraInfo = !!this.isExtraInfoMode && !hasContent && !hasOptions
    },

    isDoctorSelectionLevel(category) {
      if (!category || !Array.isArray(category.children) || category.children.length === 0) {
        return false
      }

      return category.children.every(child => !Array.isArray(child.children) || child.children.length === 0)
    },

    getDoctorListTitle(category) {
      if (!category || !category.categoryName) {
        return '所有科別醫師列表'
      }

      if (category.categoryName === '查看所有科別醫師') {
        return '所有科別醫師列表'
      }

      return `${category.categoryName}醫師列表`
    },

    invalidateAvailableDates() {
      this.apiAvailableDates = null
      this.availableDatesFromApi = []
      Object.assign(this.context, {
        selectedDate: null,
        selectedTimeSlot: null
      })
    },

    getPostRegistrantStep() {
      if (this.publicNotesFromApi && this.publicNotesFromApi.length > 0) {
        return 'user-notes'
      }
      return 'confirmation'
    },

    async routeToRegistrantSelection(backStep, forcedReturnStep = null) {
      const returnStep = forcedReturnStep || this.getPostRegistrantStep()
      Object.assign(this.context, {
        registrantBackStep: backStep,
        registrantReturnStep: returnStep
      })
      this.saveContext()

      const isLoggedIn = await this.checkLoginStatus()
      const targetRoute = isLoggedIn ? 'TongxinFamilySelection' : 'TongxinFamilyMeAdd'

      this.$router.push({
        name: targetRoute,
        query: {
          ...this.$route.query,
          bookingFlow: '1',
          returnStep,
          backStep,
          org_code: this.$route.params.org_code,
          categoryId: this.$route.params.categoryId || ''
        }
      })
    },

    getSelectedPatientDisplayName() {
      const registrant = this.context.registrant
      if (!registrant || !registrant.name) {
        return '王小明 (本人)'
      }

      if (registrant.relationship) {
        return `${registrant.name} (${registrant.relationship})`
      }

      return registrant.name
    },

    // ===== API 相關方法 =====

    /**
     * 在 category 樹中遞迴搜尋指定的 categoryId
     */
    findCategoryById(categories, targetId) {
      if (!categories || !Array.isArray(categories)) return null

      for (const cat of categories) {
        if (cat.categoryId === targetId) {
          return cat
        }
        if (cat.children && cat.children.length > 0) {
          const found = this.findCategoryById(cat.children, targetId)
          if (found) return found
        }
      }
      return null
    },

    /**
     * 在 category 樹中搜尋指定 categoryId 的 parent
     */
    findParentCategory(categories, targetId, parent = null) {
      if (!categories || !Array.isArray(categories)) return null

      for (const cat of categories) {
        if (cat.categoryId === targetId) {
          return parent
        }
        if (cat.children && cat.children.length > 0) {
          const found = this.findParentCategory(cat.children, targetId, cat)
          if (found !== null) return found
        }
      }
      return null
    },

    /**
     * @description 在 category 樹中尋找指定 categoryId 的完整路徑
     * @param {Array} categories - 目前走訪的分類陣列
     * @param {String} targetId - 目標 categoryId
     * @param {Array} currentPath - 遞迴累計的路徑
     * @returns {Array|null} 包含經過的所有 category 物件陣列
     */
    findCategoryPath(categories, targetId, currentPath = []) {
      if (!categories || !Array.isArray(categories)) return null

      for (const cat of categories) {
        const path = [...currentPath, cat]

        if (cat.categoryId === targetId) {
          return path
        }

        if (cat.children && cat.children.length > 0) {
          const foundPath = this.findCategoryPath(cat.children, targetId, path)
          if (foundPath) return foundPath
        }
      }
      return null
    },

    /**
     * 載入健檢分類資料
     */
    async loadCategories(type = null, categoryId = null) {
      try {
        const response = await tongxinApi.getCategories(this.clinicId, type, categoryId)
        this.apiCategories = response
        return this.apiCategories
      } catch (error) {
        console.error('Failed to load categories:', error)
        throw error
      }
    },

    /**
     * 載入子分類詳細資料
     */
    async loadSubcategoryDetail(subcategoryId) {
      try {
        const response = await tongxinApi.getSubcategoryDetail(subcategoryId, this.clinicId)
        this.apiSubcategoryDetail = response
        return this.apiSubcategoryDetail
      } catch (error) {
        console.error('Failed to load subcategory detail:', error)
        throw error
      }
    },

    /**
     * 載入可預約日期時段
     */
    async loadAvailableDates(rowId, dateFrom = null, dateTo = null) {
      try {
        const response = await tongxinApi.getAvailableDates(rowId, this.clinicId, dateFrom, dateTo)
        this.apiAvailableDates = response
        return this.apiAvailableDates
      } catch (error) {
        console.error('Failed to load available dates:', error)
        throw error
      }
    },

    // ===== 資料轉換方法 =====

    /**
     * 抓出本月與次月的門診表
     */
    extractScheduleImages(sourceImages) {
      // sourceImages sample format:
      // [
      //   {
      //     "alt": "2026.4_pus shin_1200x1200.jpg",
      //     "seq": 1,
      //     "src": "https://eip.tongxin.com.tw/WebImage/LineOA/ClinicScheduleManagementImg/40c2cd66-de53-48f9-96ba-bee468298f0d.jpg",
      //     "sysInfo": "20264",
      //     "title": "2026.4_pus shin_1200x1200.jpg"
      //   },
      //   ...
      // ]

      if (!this.isOutpatientSource || sourceImages.length === 0) {
        return []
      }

      const matchedImages = []

      const now = moment()
      // 本月門診表
      const outpatientScheduleThisMonth = sourceImages.find(img => img.sysInfo === now.format('YYYYM'))
      if (outpatientScheduleThisMonth) {
        matchedImages.push(outpatientScheduleThisMonth)
      }
      // 次月門診表
      if (now.date() >= 20) {
        const outpatientScheduleNextMonth = sourceImages.find(img => img.sysInfo === now.add(1, 'month').format('YYYYM'))
        if (outpatientScheduleNextMonth) {
          matchedImages.push(outpatientScheduleNextMonth)
        }
      }

      return matchedImages.map(image => ({ url: image.src }))
    },

    /**
     * 依 sortOrder 由小到大排序；若缺值則排在最後，並維持原始順序。
     */
    sortItemsBySortOrder(items) {
      if (!Array.isArray(items)) return []

      return items
        .map((item, index) => {
          const parsedOrder = Number(item && item.sortOrder)
          const sortOrder = Number.isFinite(parsedOrder) ? parsedOrder : Number.MAX_SAFE_INTEGER

          return { item, index, sortOrder }
        })
        .sort((a, b) => {
          if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder
          return a.index - b.index
        })
        .map(sortedItem => sortedItem.item)
    },

    /**
     * 僅依第一層 sortOrder 由小到大排序；若缺值則排在最後，並維持原始順序。
     */
    sortCategoriesBySortOrder(categories) {
      return this.sortItemsBySortOrder(categories)
    },

    /**
     * 將 API categories 轉換為 SelectionStep 需要的 items 格式
     */
    transformCategoriesToItems(data) {
      if (!data || !data.categories) return []

      return data.categories.map(cat => ({
        label: cat.categoryName,
        value: cat.categoryId,
        description: cat.description || '',
        icon: cat.iconUrl || null,
        color: '#207868',
        hasChildren: cat.hasChildren || false,
        childrenCount: cat.childrenCount || 0,
      }))
    },

    /**
     * 將 API subcategory detail 轉換為 Info component 需要的 data 格式
     *
     * Block type 優先以 type 字串判斷（textOnly/titleAndText/toggle/price/image/file/singleLine），
     * 若為 UUID（後端尚未轉換），則降回結構特徵偵測。
     * toggle key 支援兩種拼法：toggle（規格）/ toogle（實際 API typo）。
     * blocks 依 order 欄位排序後再渲染。
     */
    transformSubcategoryToInfo(detail) {
      if (!detail) return null

      // 清除 HTML tags 的輔助函式
      const stripHtml = (str) => {
        if (!str) return ''
        return str.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
      }
      const stripLiTags = (str) => {
        if (!str) return ''
        return str.replace(/<\/?li[^>]*>/gi, '')
      }

      // 將 notes 正規化為可顯示的金額字串（僅保留數字）
      const normalizePrice = (value) => {
        if (value === null || value === undefined) return ''
        const digits = String(value).replace(/[^0-9]/g, '')
        return digits || ''
      }

      // 將 HTML 文字轉為純文字，同時保留常見區塊/換行標籤的斷行
      const stripHtmlKeepLineBreaks = (str) => {
        if (!str) return ''

        return String(str)
          .replace(/<br\s*\/?\s*>/gi, '\n')
          .replace(/<\/(div|p|li|h[1-6])>/gi, '\n')
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/\n\s*\n+/g, '\n')
          .trim()
      }

      // 取得 toggle 資料（支援 toggle 與 toogle 兩種拼法）
      const getToggleData = (block) => {
        const data = block.toggle || block.toogle
        return Array.isArray(data) ? data : []
      }

      // 轉換 toggle group 為 InfoAccordion 期待的格式
      const transformToggleGroups = (groups) => groups.map(group => ({
        title: stripHtml(group.title) || '',
        items: Array.isArray(group.toggleItem || group.toogleItem)
          ? (group.toggleItem || group.toogleItem).map(item => ({
              name: stripHtml(item.title) || '',
              description: item.description || ''
            }))
          : []
      }))

      const blocks = []
      const outerTitle = detail.packageName || ''

      if (Array.isArray(detail.packageBlocks)) {
        // 依 order 排序
        const sorted = detail.packageBlocks.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

        sorted.forEach(block => {
          const type = block.type
          const toggleData = getToggleData(block)

          // ── 字串 type 判斷（規格標準）──────────────────────
          if (type === 'textOnly') {
            const title = stripHtml(block.title)
            const content = stripHtml(block.textContent)
            if (title || content) blocks.push({ type: 'text', title, content })

          } else if (type === 'titleAndText') {
            if (block.title || block.textContent) {
              blocks.push({
                type: 'html',
                title: block.title || '',
                content: block.textContent || '',
              })
            }

          } else if (type === 'singleLine') {
            const content = stripHtml(block.textContent)
            if (content) blocks.push({ type: 'text', title: '', content })

          } else if (type === 'toggle') {
            if (toggleData.length > 0) {
              blocks.push({
                type: 'accordion',
                title: stripHtml(block.title) || '',
                content: transformToggleGroups(toggleData)
              })
            }

          } else if (type === 'price') {
            // notes = 金額，textContent = 補充說明
            const price = normalizePrice(block.notes)
            const subtitle = stripHtmlKeepLineBreaks(block.textContent)
            if (price || subtitle) {
              blocks.push({
                type: 'price',
                title: stripHtml(block.title) || '費用',
                price,
                textContent: subtitle
              })
            }

          } else if (type === 'image') {
            blocks.push({
              type: 'html',
              title: stripHtml(block.title) || '',
              content: block.textContent,
              listUnstyled: true,
            })
          } else if (type === 'file') {
            blocks.push({
              type: 'html',
              title: stripHtml(block.title) || '',
              content: block.textContent,
              listUnstyled: false,
            })
          } else if (type === 'pageTitle') {
            blocks.push({
              type: 'pageTitle',
              title: stripHtml(block.title) || '',
              textContent: stripHtml(block.textContent) || '',
            })
          } else {
            if (toggleData.length > 0) {
              // 結構偵測：有 toogle/toggle 陣列 → accordion
              blocks.push({
                type: 'accordion',
                title: stripHtml(block.title) || '',
                content: transformToggleGroups(toggleData)
              })
            } else {
              const notesValue = normalizePrice(block.notes)
              if (notesValue) {
                // 結構偵測：notes 為數字 → price
                const subtitle = stripHtmlKeepLineBreaks(block.textContent)
                blocks.push({
                  type: 'price',
                  title: stripHtml(block.title) || '費用',
                  price: notesValue,
                  textContent: subtitle
                })
              } else {
                // 一般文字段落
                const title = stripHtml(block.title)
                const content = stripHtml(block.textContent)
                if (title || content) blocks.push({ type: 'text', title, content })
              }
            }
          }
        })
      }

      return {
        outer_title: outerTitle,
        subsequent_texts: blocks
      }
    },

    /**
     * 將 API options 轉換為 AddOn component 需要的格式
     */
    transformOptionsToAddOns(detail) {
      if (!detail || !Array.isArray(detail.options)) {
        return []
      }

      const sortedOptions = this.sortItemsBySortOrder(detail.options)

      return sortedOptions.map(option => {
        // 將 description 從 object array 轉換為可分欄位渲染的結構
        let detailedInfo = []
        if (Array.isArray(option.description)) {
          detailedInfo = option.description.map(item => {
            const title = item.tpccTitle || ''
            const text = item.tpccText || ''
            return { title, text }
          })
        }

        return {
          label: option.name,
          value: option.id,
          right_display_info: `${option.price} 元`,
          detailed_info: detailedInfo,
          expandable: detailedInfo.length > 0,
          price: option.price
        }
      })
    },

    /**
     * 將 API noticeBlocks 轉換為 Notice component 需要的格式
     */
    transformNoticeBlocks(blocks) {
      if (!Array.isArray(blocks)) return []

      const stripHtml = (str) => {
        if (!str) return ''
        return str.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
      }
      const stripLiTags = (str) => {
        if (!str) return ''
        return str.replace(/<\/?li[^>]*>/gi, '')
      }

      // 依 order 排序，並跳過 order < 0 的 header block（資料上 noticeTitle 已小起，該 block 為多餘標題）
      const pageTitleBlock = blocks.find(b => b && b.type === 'pageTitle')
      const sorted = blocks
        .filter(b => (b.order ?? 0) >= 0)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

      const mappedBlocks = sorted.map(block => {
        const type = block.type

        // ── 字串 type 判斷（規格標準）
        if (type === 'textOnly') {
          return {
            type: 'textOnly',
            title: stripHtml(block.title),
            content: stripHtml(block.textContent)
          }
        }

        if (type === 'titleAndText') {
          return {
            type: 'titleAndText',
            title: stripHtml(block.title),
            content: block.textContent || ''
          }
        }

        if (type === 'singleLine') {
          return {
            type: 'singleLine',
            title: '',
            content: stripHtml(block.textContent)
          }
        }

        if (type === 'toggle') {
          const toggleData = block.toggle || block.toogle
          const items = Array.isArray(toggleData)
            ? toggleData.map(item => stripHtml(item.title || item.text || '')).filter(Boolean)
            : (block.textContent || '').split('\n').map(s => s.trim()).filter(Boolean)
          return {
            type: 'toggle',
            title: stripHtml(block.title),
            items
          }
        }

        if (type === 'image') {
          return {
            type: 'image',
            title: stripHtml(block.title),
            url: block.url || '',
            content: stripLiTags(block.textContent || '')
          }
        }

        if (type === 'file') {
          return {
            type: 'file',
            title: stripHtml(block.title),
            url: block.url || '',
            content: stripLiTags(block.textContent || '')
          }
        }

        // ── UUID type fallback：依結構判斷
        const toggleData = block.toggle || block.toogle
        if (Array.isArray(toggleData) && toggleData.length > 0) {
          const items = toggleData.map(item => stripHtml(item.title || item.text || '')).filter(Boolean)
          return { type: 'toggle', title: stripHtml(block.title), items }
        }

        if (block.url) {
          return {
            type: 'image',
            title: stripHtml(block.title),
            url: block.url,
            content: stripLiTags(block.textContent || '')
          }
        }

        // 一般文字
        return {
          type: 'textOnly',
          title: stripHtml(block.title),
          content: stripHtml(block.textContent)
        }
      }).filter(b => b.title || b.content || b.url || (b.items && b.items.length > 0))

      if (pageTitleBlock && pageTitleBlock.textContent) {
        mappedBlocks.unshift({
          type: 'pageTitle',
          content: pageTitleBlock.textContent
        })
      }

      return mappedBlocks
    },

    /**
     * 將 API extraPageBinding 轉換為 CustomerServiceConfirmation 需要的格式
     */
    transformExtraPageBinding(blocks) {
      if (!Array.isArray(blocks) || blocks.length === 0) return null

      const unescapeHtml = (str) => {
        if (!str) return ''
        const entities = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" }
        return str.replace(/&amp;|&lt;|&gt;|&quot;|&#39;/g, m => entities[m])
      }

      const stripHtml = (str) => {
        if (!str) return ''
        return unescapeHtml(str.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim())
      }

      const sorted = blocks.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

      let mainTitle = this.defaultExceptionTitle
      let contentArr = []

      sorted.forEach(block => {
        const title = stripHtml(block.title)
        const content = stripHtml(block.textContent)
        if (title && mainTitle === this.defaultExceptionTitle) {
          mainTitle = title
        } else if (title) {
          contentArr.push(`【${title}】`)
        }
        if (content) {
          contentArr.push(content)
        }
      })

      return {
        title: mainTitle,
        content: contentArr.join('\n\n')
      }
    },

    /**
     * 將 API publicNotes 轉換為 UserNotes component 需要的格式
     */
    transformPublicNotes(notes) {
      if (!Array.isArray(notes)) return []

      const unescapeHtml = (str) => {
        if (!str) return ''
        const entities = {
          '&amp;': '&',
          '&lt;': '<',
          '&gt;': '>',
          '&quot;': '"',
          '&#39;': "'"
        }
        return str.replace(/&amp;|&lt;|&gt;|&quot;|&#39;/g, m => entities[m])
      }

      const stripHtml = (str) => {
        if (!str) return ''
        return unescapeHtml(str.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim())
      }

      const sorted = notes.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      return sorted.map(block => ({
        order: block.order ?? 0,
        title: stripHtml(block.title),
        content: stripHtml(block.textContent)
      }))
    },


    /**
     * 將 API availableDates 轉換為 AppointmentTimeSelector 需要的格式
     */
    transformAvailableDates(availableDatesData) {
      if (!availableDatesData || !Array.isArray(availableDatesData.avaliabelDates)) {
        return []
      }

      // 時段代碼 mapping: 1=morning, 2=afternoon, 3=evening
      const slotTypeMap = {
        1: 'morning',
        2: 'afternoon',
        3: 'evening'
      }

      // 先將日期陣列從小到大排序
      const sortedDates = availableDatesData.avaliabelDates.slice().sort((a, b) => {
        if (!a.dateValue) return 1
        if (!b.dateValue) return -1
        return a.dateValue.localeCompare(b.dateValue)
      })

      // 將 API 資料轉換為 AppointmentTimeSelector 需要的 Array 格式
      return sortedDates.map(dateItem => {
        if (!dateItem.dateValue || !Array.isArray(dateItem.slots)) {
          return null
        }

        // 確保時段也按照 1(morning), 2(afternoon), 3(evening) 的順序顯示
        const sortedSlots = dateItem.slots.slice().sort((a, b) => (a.value || 0) - (b.value || 0))

        return {
          date: dateItem.dateValue,
          timeSlots: sortedSlots.map(slot => {
            return {
              type: slotTypeMap[slot.value] || `slot-${slot.value}`,
              text: slot.text,
              value: slot.value,
              isFull: slot.status === 'full',
              isUnavailable: slot.status === 'unavailable',
            }
          })
        }
      }).filter(Boolean)
    },

    /**
     * 計算已選加值選項嘅總費用
     */
    calculateAddOnsCost() {
      if (!Array.isArray(this.context.addOns) || this.context.addOns.length === 0) {
        return 0
      }

      return this.context.addOns.reduce((total, addonId) => {
        const addon = this.addOnOptionsFromApi.find(option => option.value === addonId)
        return total + (addon ? addon.price : 0)
      }, 0)
    },

    /**
     * 載入病患資料
     */
    async loadPatients() {
      const isGuest = this.$store.getters['web/tongxin/isGuest']
      if (isGuest) {
        return { data: { patients: [] } }
      }

      try {
        const response = await tongxinApi.getPatients(this.clinicId)
        return response
      } catch (error) {
        console.error('Failed to load patients:', error)
        throw error
      }
    },

    /**
     * 建立健檢預約 (API 2.4)
     */
    async createReservation() {
      try {
        const { categoryId } = this.$route.params

        let visitDate = '1970-01-01'
        let slotValue = 1

        if (this.context.selectedTimeSlot) {
          // 解析時段資料：selectedTimeSlot 格式為 "YYYY-MM-DD-morning"
          const parts = this.context.selectedTimeSlot.split('-')
          visitDate = parts.slice(0, 3).join('-')  // YYYY-MM-DD
          const timeSlotType = parts[3]  // morning/afternoon/evening

          // 將 timeSlotType 轉換為 slotValue (1=morning, 2=afternoon, 3=evening)
          const slotValueMap = {
            'morning': 1,
            'afternoon': 2,
            'evening': 3
          }
          slotValue = slotValueMap[timeSlotType] || 1
        }

        const isGuest = this.$store.getters['web/tongxin/isGuest']

        const consent = {
          tosVer: 'v1.0',
          privacyVer: 'v1.0',
          acceptedAt: new Date().toISOString()
        }

        const payload = {
          clinicId: this.clinicId,
          rowId: categoryId,
          visitDate: visitDate,
          slotValue: slotValue,
          categoryId: categoryId,
          subcategoryId: categoryId,
          packageId: categoryId,
          optionIds: this.context.addOns || [],
          consent: consent,
          source: isGuest ? 'WEB' : 'LINE',
          remark: this.context.userNotes || null
        }

        const registrant = this.context.registrant || {}
        const isNonSelfRegistrant = ['auth', 'family'].includes(registrant.source)

        payload.patient = {
          name: registrant.name || this.context.patientName || '',
          ssid: registrant.ssid || this.context.patientSsid || '',
          birthday: registrant.birthday || this.context.patientBirthday || ''
        }

        const contact = {
          mobile: registrant.mobile || this.context.contactMobile || '',
          email: this.context.contactEmail || ''
        }

        if (contact.mobile || contact.email) {
          payload.contact = {}
          if (contact.mobile) {
            payload.contact.mobile = contact.mobile
          }
          if (contact.email) {
            payload.contact.email = contact.email
          }
        }

        if (isNonSelfRegistrant) {
          const patientBirthday = registrant.birthday || this.context.patientBirthday || ''
          if (!patientBirthday) {
            this.$swal({
              type: 'error',
              title: '親友生日不可為空，請重新選擇或編輯親友資料',
              confirmButtonText: '確定'
            })
            throw new Error('Missing non-self registrant birthday')
          }

          payload.patient = {
            name: registrant.name || this.context.patientName || '',
            ssid: registrant.ssid || this.context.patientSsid || '',
            birthday: patientBirthday
          }

          const liffUser = this.$store.state.liffAuth.user || {}
          const registrantContact = {
            lineUId: liffUser.customer_code || '',
            mobile: liffUser.mobile || liffUser.mobile_phone || this.context.contactMobile || '',
            email: this.context.contactEmail || ''
          }

          payload.contact = {}
          if (registrantContact.lineUId) {
            payload.contact.lineUId = registrantContact.lineUId
          }
          if (registrantContact.mobile) {
            payload.contact.mobile = registrantContact.mobile
          }
          if (registrantContact.email) {
            payload.contact.email = registrantContact.email
          }
        }

        console.log('Creating reservation with payload:', payload)
        const response = await tongxinApi.createReservation(payload)
        console.log('Reservation created successfully:', response)
        return response
      } catch (error) {
        console.error('Failed to create reservation:', error)
        throw error
      }
    },
  },

  // Lifecycle hooks
  watch: {
    '$route.params': {
      handler(newParams) {
        if (newParams.stepId) {
          this.enterNode(newParams.stepId)
        }
      }
    },
    context: {
      deep: true,
      handler() {
        this.saveContext();
      }
    },
    localValue: {
      handler() {
        this.saveContext();
      }
    }
  },

  mounted() {
    this.loadContext()
    this.initializeMockSteps()

    // 預先檢查登入狀態
    this.checkLoginStatus()

    // 初始化完成後，手動觸發第一次 enterNode
    if (this.$route.params.stepId) {
      this.enterNode(this.$route.params.stepId)
    }
  },

  beforeDestroy() {
    this.saveContext()
  },
}
